import { type ReactNode } from "react";
import Reveal from "@/components/feature/Reveal";
import {
  LINE_URL,
  LINE_COLOR,
  TEL_DISPLAY,
  TEL_HREF,
  FORM_ANCHOR,
  HOURS,
  HERO_MAIN_IMAGE,
} from "@/constants";

type AnimProps = {
  on: boolean;
  delay?: number;
  children: ReactNode;
};

/** PC（重ねレイアウト）のときだけ、控えめに順番表示するラッパー */
function Anim({ on, delay = 0, children }: AnimProps) {
  if (!on) return <>{children}</>;
  return <Reveal delay={delay}>{children}</Reveal>;
}

/** ファーストビューの文字情報（PCでは画像の上に重ね、SPでは画像の下に表示） */
function HeroCopy({ animate = false }: { animate?: boolean }) {
  return (
    <div className="max-w-[560px] md:max-w-[460px]">
      <Anim on={animate} delay={150}>
        <p className="inline-flex items-center gap-2 rounded-full border-2 border-primary-300 bg-primary-50 px-4 py-2 text-sm font-black text-primary-800 md:text-base">
          <i className="ri-map-pin-2-fill text-lg" />
          埼玉県春日部市｜地域密着の不用品回収・遺品整理
        </p>

        <h1 className="mt-5 font-heading text-[1.8rem] font-black leading-[1.25] tracking-tight text-foreground-950 md:text-[2.6rem] md:leading-[1.16]">
          <span className="block">春日部市の</span>
          <span className="block text-primary-700">不用品回収・遺品整理</span>
        </h1>
      </Anim>

      <Anim on={animate} delay={340}>
        <div className="mt-5 inline-flex flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-lg border-2 border-accent-300 bg-accent-50 px-4 py-3">
          <span className="text-sm font-black text-accent-800 md:text-base">少量なら</span>
          <span className="font-heading text-3xl font-black leading-none text-accent-700 md:text-4xl">
            5,500<span className="text-lg">円</span>〜
          </span>
          <span className="text-xs font-bold text-accent-700 md:text-sm">（家具1・2点など）</span>
        </div>
      </Anim>

      <Anim on={animate} delay={460}>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-700 md:max-w-[360px] md:text-lg">
          <br />
        </p>
      </Anim>

      <Anim on={animate} delay={560}>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-black text-background-50 md:text-base">
            <i className="ri-check-double-line text-lg" />
            見積無料
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-2.5 text-sm font-black text-background-50 md:text-base">
            <i className="ri-truck-line text-lg" />
            出張費無料
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-bold text-foreground-700 md:text-base">
            <i className="ri-time-line text-lg text-primary-600" />
            {HOURS}
          </span>
        </div>
      </Anim>

      <Anim on={animate} delay={680}>
        <div className="mt-6 flex flex-col gap-3.5 sm:flex-row">
          <a
            href={LINE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ backgroundColor: LINE_COLOR }}
            className="flex flex-[1.4] cursor-pointer items-center justify-center gap-2.5 rounded-lg px-6 py-5 font-heading text-lg font-black text-white transition-all hover:brightness-95"
          >
            <i className="ri-line-fill text-2xl" />
            <span className="whitespace-nowrap">LINEで無料見積</span>
          </a>
          <a
            href={TEL_HREF}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2.5 rounded-lg bg-primary-600 px-6 py-5 font-heading text-lg font-black text-background-50 transition-all hover:bg-primary-700"
          >
            <i className="ri-phone-fill text-2xl" />
            <span className="whitespace-nowrap">電話で相談</span>
          </a>
        </div>
        <a
          href={FORM_ANCHOR}
          className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-primary-300 bg-background-50 px-6 py-3.5 font-heading text-base font-black text-primary-700 transition-all hover:bg-primary-50"
        >
          <i className="ri-mail-send-line text-xl" />
          <span className="whitespace-nowrap">フォームで見積もりはこちら</span>
        </a>
        <p className="mt-4 flex items-start gap-2 text-sm font-bold text-foreground-600">
          <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" />
          写真を送るだけでもOK。しつこい営業は行いません。お電話は {TEL_DISPLAY} まで。
        </p>
      </Anim>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative bg-background-50">
      {/* メインビジュアル：SPは通常配置、PC（md以上）は文字を重ねる一体型のファーストビュー */}
      <div className="relative overflow-hidden md:min-h-[620px] lg:min-h-[680px]">
        <img
          src={HERO_MAIN_IMAGE.src}
          alt={HERO_MAIN_IMAGE.alt}
          className="hero-fade block h-auto w-full md:absolute md:inset-0 md:h-full md:w-full md:object-cover md:object-[50%_38%]"
        />

        {/* PCのみ：画像を暗くせず、左側をごく薄くなじませて文字を読みやすくする */}
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-background-50 from-0% via-background-50/92 via-42% to-transparent to-[72%] md:block" />

        {/* PCのみ：画像の上に文字情報を重ねる */}
        <div className="absolute inset-0 hidden items-center md:flex">
          <div className="site-container w-full">
            <HeroCopy animate />
          </div>
        </div>
      </div>

      {/* スマートフォン：画像の下に順番に表示（現在の表示を維持） */}
      <div className="site-container py-8 md:hidden">
        <Reveal>
          <HeroCopy />
        </Reveal>
      </div>
    </section>
  );
}